import { Callout } from "$components/callout";
import {
  alpha,
  Box,
  Checkbox,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  styled,
  Typography,
} from "@material-ui/core";
import { FormattedMessage } from "react-intl";

const StyledOuterContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  width: "100%",
});

const StyledCallout = styled(Callout)({
  borderBottomLeftRadius: "0px",
  borderBottomRightRadius: "0px",
  width: "100%",
});

const FormWrapper = styled("div")(({ theme }) => ({
  backgroundColor: alpha(theme.palette.warning.main, 0.2),
  borderBottomLeftRadius: "4px",
  borderBottomRightRadius: "4px",
  padding: "4px 16px",
  marginBottom: "8px",
}));

const StyledInnerBox = styled(Box)(() => ({
  width: "100%",
  display: "flex",
  justifyContent: "space-between",
  minWidth: "100%",
}));

const BoldText = styled(Typography)(() => ({
  fontWeight: "bold",
}));

const FormContainer = styled(Box)(() => ({
  paddingTop: "12px",
}));

export const MalarialNudgeMessages = {
  POSITIVE_DIAGNOSTIC_RESULT_WITHOUT_ANTI_MALARIAL:
    "This patient had a positive malaria result today. This is not an eligible malaria drug.",
  NEGATIVE_DIAGNOSTIC_RESULT_WITH_ANTI_MALARIAL:
    "This patient had a negative malaria test today. Check this medication is needed.",
} as const;

export const MalarialPrecriptionNudgeFormOptions = {
  patient_insists: "The patient insists on getting this medication",
  avoid_complications:
    "To avoid complications and prevent the condition getting worse",
  suspect_malaria: "I still think this person has malaria",
  other: "Other",
} as const;

const AddButtonText = styled(Typography)(({ theme }) => ({
  fontWeight: "bold",
  marginRight: "8px",
  color: theme.palette.primary.main,
}));

const AddDiagnosisActionButton = ({
  onAddDiagnosis,
}: {
  onAddDiagnosis: () => void;
}) => (
  <AddButtonText
    role="button"
    variant="body1"
    align="center"
    style={{ fontWeight: "bold", cursor: "pointer" }}
    onClick={onAddDiagnosis}
    onKeyDown={(e) => e.key === "Enter" && onAddDiagnosis()}
  >
    <FormattedMessage
      id="nudge.diagnosis.add.action"
      defaultMessage="Add a diagnosis"
    />
  </AddButtonText>
);

export const AntibioticPrescriptionNudgeInfoBox = ({
  onAddDiagnosis,
}: {
  onAddDiagnosis?: () => void;
}) => (
  <Callout
    type="info"
    data-testid="antibiotic-prescription-nudge-info-box"
    action={
      onAddDiagnosis ? (
        <AddDiagnosisActionButton onAddDiagnosis={onAddDiagnosis} />
      ) : null
    }
  >
    <StyledInnerBox>
      <Typography>
        <FormattedMessage
          id="nudge.diagnosis.info.box"
          defaultMessage="This patient doesn't have a recent bacterial diagnosis."
        />
      </Typography>
    </StyledInnerBox>
  </Callout>
);

export const MalarialPrescriptionNudgeWarningBox = ({
  message,
  nudgeSurveyState,
  setNudgeSurveyState,
}: {
  message: string;
  nudgeSurveyState?: Record<
    keyof typeof MalarialPrecriptionNudgeFormOptions,
    boolean
  >;
  setNudgeSurveyState?: (
    state: Record<keyof typeof MalarialPrecriptionNudgeFormOptions, boolean>,
  ) => void;
}) => {
  return (
    <StyledOuterContainer>
      <StyledCallout
        type="warning"
        data-testid="malarial-prescription-nudge-warning-box"
      >
        <Typography>{message}</Typography>
      </StyledCallout>

      {!!setNudgeSurveyState && (
        <FormWrapper>
          <Divider />
          <FormContainer>
            <BoldText>
              <FormattedMessage
                id="nudge.diagnosis.info.box"
                defaultMessage="Can you share why you’ve decided to give this medication?"
              />
            </BoldText>
            <List>
              {Object.entries(MalarialPrecriptionNudgeFormOptions).map(
                ([key, text]) => (
                  <ListItem
                    key={key}
                    role="listitem"
                    button
                    onClick={() =>
                      setNudgeSurveyState({
                        ...nudgeSurveyState,
                        [key]: !nudgeSurveyState?.[key],
                      })
                    }
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        tabIndex={-1}
                        disableRipple
                        checked={!!nudgeSurveyState?.[key]}
                      />
                    </ListItemIcon>
                    <ListItemText>{text}</ListItemText>
                  </ListItem>
                ),
              )}
            </List>
          </FormContainer>
        </FormWrapper>
      )}
    </StyledOuterContainer>
  );
};
